import React, { useState } from 'react';

export interface TableColumn<T> {
  key: string;
  label: string;
  sortable?: boolean;
  render?: (item: T, index: number) => React.ReactNode;
  className?: string;
  width?: string;
}

export interface DataTableProps<T> {
  data: T[];
  columns: Array<TableColumn<T>>;
  keyExtractor: (item: T, index: number) => string | number;
  sortable?: boolean;
  defaultSortField?: string;
  defaultSortDirection?: 'asc' | 'desc';
  className?: string;
  emptyMessage?: string;
  loading?: boolean;
  onSort?: (field: string, direction: 'asc' | 'desc') => void;
}

type SortDirection = 'asc' | 'desc';

export default function DataTable<T>({
  data,
  columns,
  keyExtractor,
  sortable = false,
  defaultSortField,
  defaultSortDirection = 'asc',
  className = '',
  emptyMessage = 'No data available',
  loading = false,
  onSort,
}: DataTableProps<T>) {
  const [sortField, setSortField] = useState<string>(defaultSortField ?? '');
  const [sortDirection, setSortDirection] = useState<SortDirection>(defaultSortDirection);

  const handleSort = (field: string) => {
    if (!sortable) return;

    const column = columns.find((col) => col.key === field);
    if (!column?.sortable) return;

    let newDirection: SortDirection = 'asc';
    if (sortField === field) {
      newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    }

    setSortField(field);
    setSortDirection(newDirection);

    if (onSort) {
      onSort(field, newDirection);
    }
  };

  const getSortedData = () => {
    if (!sortable || !sortField) return data;

    return [...data].sort((a, b) => {
      const modifier = sortDirection === 'asc' ? 1 : -1;

      // Get values for comparison
      let aValue: any = (a as any)[sortField];
      let bValue: any = (b as any)[sortField];

      // Handle special cases for nested properties or custom sorting
      if (sortField === 'progress' && Array.isArray(aValue) && Array.isArray(bValue)) {
        aValue = aValue.length;
        bValue = bValue.length;
      }

      // String comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return aValue.localeCompare(bValue) * modifier;
      }

      // Numeric comparison
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return (aValue - bValue) * modifier;
      }

      // Default comparison
      return String(aValue).localeCompare(String(bValue)) * modifier;
    });
  };

  const renderSortIndicator = (column: TableColumn<T>) => {
    if (!sortable || !column.sortable) return null;
    if (sortField !== column.key) return null;
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  if (loading) {
    return <div className="text-center py-4">Loading...</div>;
  }

  if (data.length === 0) {
    return <div className="text-center py-4 text-gray-500">{emptyMessage}</div>;
  }

  const sortedData = getSortedData();

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          {/* Column groups for fixed widths if specified */}
          {columns.some((col) => col.width) && (
            <colgroup>
              {columns.map((column, index) => (
                <col key={index} className={column.width} />
              ))}
            </colgroup>
          )}

          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-700 uppercase tracking-wider ${
                    sortable && column.sortable ? 'cursor-pointer hover:bg-gray-100' : ''
                  } ${column.className ?? ''}`}
                  onClick={() => {
                    handleSort(column.key);
                  }}
                >
                  {column.label}
                  {renderSortIndicator(column)}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-200">
            {sortedData.map((item, index) => (
              <tr key={keyExtractor(item, index)} className="hover:bg-gray-50">
                {columns.map((column) => (
                  <td key={column.key} className={`px-6 py-2 text-sm text-gray-500 ${column.className ?? ''}`}>
                    {column.render ? column.render(item, index) : String((item as any)[column.key] ?? '')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
